import React from 'react';
import { useNavigate } from 'react-router-dom';

const TermsOfService: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-brand">
            <div className="brand-logo">NS</div>
            <span className="brand-text">NexSync</span>
          </div>
          <div className="navbar-actions">
            <button 
              onClick={() => navigate('/')}
              className="btn btn-ghost"
            >
              Home
            </button>
            <button 
              onClick={() => navigate('/signin')}
              className="btn btn-ghost"
            >
              Sign in
            </button>
            <button 
              onClick={() => navigate('/signup')}
              className="btn btn-primary"
            >
              Sign up
            </button>
          </div>
        </div>
      </nav>

      {/* Terms of Service Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
          <p className="text-lg text-gray-600">
            Last Updated: November 2024
          </p>
          <div className="mt-2 text-sm text-gray-500">
            Please read these terms carefully before using our services
          </div>
        </div>

        {/* Important Notice */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.998-.833-2.732 0L4.342 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">Important Notice</h3>
              <p className="text-yellow-700">
                By accessing or using the NexSync platform, you agree to be bound by these 
                Terms of Service. If you disagree with any part of the terms, you may not 
                access the service.
              </p>
            </div>
          </div>
        </div>

        {/* Terms Content */}
        <div className="bg-white rounded-xl shadow-md p-8 space-y-12">
          {/* Agreement */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <div className="space-y-4 text-gray-700">
              <p>
                These Terms of Service constitute a legally binding agreement made between you, 
                whether personally or on behalf of an entity ("you") and NexSync ("we," "us," or "our"), 
                concerning your access to and use of the NexSync platform.
              </p>
              <p>
                You agree that by accessing the platform, you have read, understood, and agree 
                to be bound by all of these Terms of Service. If you do not agree with all of 
                these Terms of Service, then you are expressly prohibited from using the platform 
                and you must discontinue use immediately.
              </p>
            </div>
          </section>

          {/* User Accounts */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. User Accounts</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Registration</h3>
                <p className="text-gray-700 mb-3">
                  To use certain features of the platform, you must register for an account 
                  and provide accurate, complete, and current information. You agree to:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-gray-700">
                  <li>Maintain the security of your password and accept all risks of unauthorized access</li>
                  <li>Promptly notify us if you discover or suspect any security breaches</li>
                  <li>Take responsibility for all activities that occur under your account</li>
                  <li>Not create accounts for others without their permission</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Account Types</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Student</h4>
                    <p className="text-gray-600 text-sm">
                      Post projects, hire freelancers, and manage academic work
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Freelancer</h4>
                    <p className="text-gray-600 text-sm">
                      Apply for projects, deliver work, and receive payments
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Client</h4>
                    <p className="text-gray-600 text-sm">
                      Post projects, review proposals, and manage collaborations
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Services */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Services Provided</h2>
            <div className="space-y-4 text-gray-700">
              <p>
                NexSync provides a platform that connects students and clients with freelancers 
                for academic project work. We are not a party to any agreement between users 
                and do not guarantee the quality, safety, or legality of services provided.
              </p>
              
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Our Role</h3>
                <p className="text-gray-700">
                  We act as an intermediary platform facilitating connections between users. 
                  We are not responsible for:
                </p>
                <ul className="list-disc pl-5 space-y-2 mt-3 text-gray-700">
                  <li>The quality of work delivered by freelancers</li>
                  <li>Payment disputes between users</li>
                  <li>Misrepresentation of skills or qualifications</li>
                  <li>Project outcomes or academic results</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Payments and Fees */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Payments and Fees</h2>
            <div className="space-y-6">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-200 px-4 py-3 text-left">Service</th>
                      <th className="border border-gray-200 px-4 py-3 text-left">Fee</th>
                      <th className="border border-gray-200 px-4 py-3 text-left">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-200 px-4 py-3 font-medium">Platform Fee</td>
                      <td className="border border-gray-200 px-4 py-3">15%</td>
                      <td className="border border-gray-200 px-4 py-3">
                        Service charge on all completed transactions
                      </td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-200 px-4 py-3 font-medium">Payment Processing</td>
                      <td className="border border-gray-200 px-4 py-3">2.9%</td>
                      <td className="border border-gray-200 px-4 py-3">
                        Third-party payment processing fees
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-4 py-3 font-medium">Withdrawal</td>
                      <td className="border border-gray-200 px-4 py-3">₦100</td>
                      <td className="border border-gray-200 px-4 py-3">
                        Per withdrawal transaction to local bank
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">Payment Terms</h3>
                <ul className="space-y-2 text-yellow-700">
                  <li>• All payments are processed through our secure payment gateway</li>
                  <li>• Funds are held in escrow until project completion</li>
                  <li>• Refunds are subject to our refund policy and dispute resolution process</li>
                  <li>• Late payments may result in account suspension</li>
                </ul>
              </div>
            </div>
          </section>

          {/* User Conduct */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. User Conduct</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Prohibited Activities</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-red-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-red-800 mb-2">Academic Integrity</h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li>• Plagiarism or academic dishonesty</li>
                      <li>• Submitting work that is not your own</li>
                      <li>• Violating institutional academic policies</li>
                    </ul>
                  </div>
                  
                  <div className="bg-red-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-red-800 mb-2">Platform Misuse</h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li>• Harassment or discrimination</li>
                      <li>• Spamming or fraudulent activities</li>
                      <li>• Circumventing payment systems</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Content Standards</h3>
                <p className="text-gray-700 mb-4">
                  You agree not to post, upload, or transmit any content that:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-gray-700">
                  <li>Is unlawful, fraudulent, or misleading</li>
                  <li>Infringes on intellectual property rights</li>
                  <li>Contains viruses or malicious code</li>
                  <li>Violates the privacy rights of others</li>
                  <li>Is pornographic, obscene, or offensive</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Intellectual Property</h2>
            <div className="space-y-4 text-gray-700">
              <p>
                The platform and its original content, features, and functionality are owned 
                by NexSync and are protected by international copyright, trademark, patent, 
                trade secret, and other intellectual property laws.
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">User Content</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">License to NexSync</h4>
                    <p className="text-gray-600 text-sm">
                      By posting content, you grant us a worldwide, non-exclusive, royalty-free 
                      license to use, reproduce, and display such content for platform operations.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Project Deliverables</h4>
                    <p className="text-gray-600 text-sm">
                      Ownership of project deliverables is transferred to the client upon 
                      full payment, unless otherwise agreed in writing.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Dispute Resolution */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Dispute Resolution</h2>
            <div className="space-y-6">
              <div className="bg-green-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Our Process</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">1. Direct Resolution</h4>
                    <p className="text-gray-600 text-sm">
                      Users are encouraged to resolve disputes directly through communication 
                      and negotiation.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">2. Mediation</h4>
                    <p className="text-gray-600 text-sm">
                      If direct resolution fails, parties may request mediation through 
                      our platform moderators.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">3. Arbitration</h4>
                    <p className="text-gray-600 text-sm">
                      Unresolved disputes may be submitted to binding arbitration in 
                      accordance with Nigerian law.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Refund Policy</h3>
                <p className="text-gray-700">
                  Refunds may be issued in cases of:
                </p>
                <ul className="list-disc pl-5 space-y-1 mt-2 text-gray-700">
                  <li>Non-delivery of agreed services</li>
                  <li>Significant deviation from project requirements</li>
                  <li>Proven fraud or misrepresentation</li>
                  <li>Platform error or technical failure</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
            <div className="space-y-4 text-gray-700">
              <p>
                To the maximum extent permitted by law, NexSync shall not be liable for any 
                indirect, incidental, special, consequential, or punitive damages, including 
                without limitation, loss of profits, data, use, goodwill, or other intangible 
                losses.
              </p>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <p className="text-yellow-700">
                  <strong>Important:</strong> Our maximum aggregate liability to you for all 
                  claims arising out of or relating to these terms or your use of the platform 
                  shall not exceed the greater of ₦50,000 or the amount you have paid us in 
                  the past twelve months.
                </p>
              </div>
            </div>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Termination</h2>
            <div className="space-y-4 text-gray-700">
              <p>
                We may terminate or suspend your account immediately, without prior notice 
                or liability, for any reason whatsoever, including without limitation if you 
                breach the Terms.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">User Termination</h4>
                  <p className="text-gray-600 text-sm">
                    You may terminate your account at any time by contacting customer support. 
                    Upon termination, your right to use the platform will immediately cease.
                  </p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Survival</h4>
                  <p className="text-gray-600 text-sm">
                    All provisions of the Terms which by their nature should survive 
                    termination shall survive, including ownership provisions, warranty 
                    disclaimers, and limitations of liability.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Governing Law</h2>
            <div className="space-y-4 text-gray-700">
              <p>
                These Terms shall be governed and construed in accordance with the laws of 
                the Federal Republic of Nigeria, without regard to its conflict of law provisions.
              </p>
              
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Jurisdiction</h3>
                <p className="text-gray-700">
                  Any legal suit, action, or proceeding arising out of, or related to, these 
                  Terms or the platform shall be instituted exclusively in the federal courts 
                  of Nigeria or the courts of Lagos State.
                </p>
              </div>
            </div>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to Terms</h2>
            <div className="space-y-4 text-gray-700">
              <p>
                We reserve the right, at our sole discretion, to modify or replace these Terms 
                at any time. If a revision is material, we will provide at least 30 days' notice 
                prior to any new terms taking effect.
              </p>
              
              <div className="bg-green-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Your Responsibility</h3>
                <p className="text-gray-700">
                  It is your responsibility to periodically review these Terms for changes. 
                  Your continued use of the platform after any modifications constitutes 
                  acceptance of those changes.
                </p>
              </div>
            </div>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Information</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="space-y-2">
                <p className="font-medium text-gray-900">Email: legal@nexsync.com</p>
                <p className="font-medium text-gray-900">Phone: +234 800 123 4567</p>
                <p className="font-medium text-gray-900">
                  Address: 123 Innovation Drive, Victoria Island, Lagos, Nigeria
                </p>
              </div>
            </div>
          </section>

          {/* Acceptance */}
          <div className="border-t border-gray-200 pt-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">Acceptance of Terms</h3>
                <p className="text-gray-600">
                  By using our platform, you acknowledge that you have read and agree to 
                  these Terms of Service.
                </p>
              </div>
              <div className="text-sm text-gray-500">
                Effective Date: November 1, 2024
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between mt-8">
          <button 
            onClick={() => navigate('/privacypolicy')}
            className="btn btn-secondary"
          >
            ← Privacy Policy
          </button>
          <button 
            onClick={() => navigate('/cookiepolicy')}
            className="btn btn-primary"
          >
            Cookie Policy →
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="navbar-brand">
                <div className="brand-logo">NS</div>
                <span className="brand-text">NexSync</span>
              </div>
              <p className="footer-description">
                Terms govern your use of our platform.
              </p>
            </div>
            
            <div className="footer-links">
              <div className="footer-column">
                <h4>Legal</h4>
                <button onClick={() => navigate('/privacypolicy')} className="footer-link">Privacy Policy</button>
                <button onClick={() => navigate('/termsofservice')} className="footer-link">Terms of Service</button>
                <button onClick={() => navigate('/cookiepolicy')} className="footer-link">Cookie Policy</button>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} NexSync. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;